import { FormikErrors, FormikTouched } from "formik";
import React, { useState, useRef } from "react";
import styled from "styled-components";
import { DropdownHandler } from "./DropdownHandler";
import { Dropdown, DropdownContainer, DropdownElement, Option, OptionText } from "./SelectMenu";
import { TextArea } from "./TextArea";
import { BaseInput } from "./TextInputUtils";
import { InputWrapper, Wrapper } from "./TextInputUtils";
import { KEY_ARROW_DOWN } from "./constants/keyboardCodes";
import { useKeyboardNav } from "./hooks/useKeyboardNav";
import { usePortalDropdownCoords } from "./hooks/usePortalDropdownCoords";
import { mediaQueries, style } from "./style";

export type AutocompleteInputProps = {
  onValueInput?: (currentValue: string) => void;
  onSuggestionClick: (suggesting: string) => void;
  mutateSuggestion?: (suggestion: string, currentValue: string) => string;
  suggestions: string[];
  showSuggestions?: boolean;
  compound?: boolean;
  name: string;
  label: string;
  labelTooltip?: string;
  description?: React.ReactNode;
  value: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | FormikErrors<any>;
  touched?: boolean | FormikTouched<any>;
  mono?: boolean;
};

const ValueInput = styled(TextArea)`
  height: auto;
  margin-bottom: ${style.vars.space["2"]};

  ${InputWrapper} {
    height: 48px;
  }

  ${BaseInput} {
    padding: 0.7rem ${style.vars.space["3"]};
    min-height: auto;
  }

  ${mediaQueries.mediumMin} {
    min-width: 300px;
    margin-bottom: 0;
  }

  ${mediaQueries.smallMax} {
    margin-left: 0;
  }
`;

const SuggestionsDropdown = styled(DropdownElement)`
  box-shadow: ${style.vars.boxShadow.base};
`;

/**
 * @deprecated Use `TextInputV2.Autocomplete` instead
 */
export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  compound = false,
  suggestions = [],
  name,
  mono,
  label,
  labelTooltip,
  required,
  disabled,
  value,
  error,
  touched,
  placeholder,
  onValueInput,
  onSuggestionClick,
  mutateSuggestion,
  showSuggestions,
  ...inputProps
}) => {
  const [isFocus, setIsFocus] = useState(false);
  const dropdownElementRef = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;

  const { isMenuOpen, menuRef, onKeyUp, inputRef, onComboBoxKeyDown, onItemKeyUp, open, close, rover } = useKeyboardNav({
    isMenuOpen: false,
  });
  const { coords, updateCoords, shouldUseCoords } = usePortalDropdownCoords({
    menuRef,
    isMenuOpen,
    renderInPortal: false,
  });

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    onComboBoxKeyDown(event);
    open();
    if (event.key == KEY_ARROW_DOWN && suggestions.length > 0) {
      const firstOption = dropdownElementRef?.current?.firstElementChild as HTMLElement;
      firstOption?.focus();
    }
  };

  return (
    <Wrapper
      onFocus={() => {
        open();
        setIsFocus(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsFocus(false);
        }
      }}
    >
      <ValueInput
        id={name}
        placeholder={placeholder}
        name={name}
        error={error}
        mono={mono}
        required={required}
        disabled={disabled}
        label={label}
        labelTooltip={labelTooltip}
        touched={touched}
        value={value}
        innerRef={inputRef}
        autoComplete="off"
        onKeyDown={handleOnKeyDown}
        onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
          e.stopPropagation();
          !compound && onKeyUp(e);
          onValueInput && onValueInput(e.currentTarget.value);
        }}
        {...inputProps}
      />
      {showSuggestions && isFocus && suggestions.length > 0 && (
        <DropdownContainer>
          <DropdownHandler
            mobileAlignment="fixed-bottom"
            clickAway={(e) => {
              !isFocus && close();
              e.stopPropagation();
            }}
            inProp={isMenuOpen}
            renderInPortal={false}
          >
            <Dropdown coords={coords} updateCoords={updateCoords} shouldUseCoords={shouldUseCoords}>
              <SuggestionsDropdown ref={dropdownElementRef}>
                {suggestions.map((suggestion) => (
                  <Option
                    role="menuitem"
                    key={suggestion}
                    onKeyUp={onItemKeyUp}
                    onClick={() => {
                      const mutatedSuggestion = mutateSuggestion ? mutateSuggestion(suggestion, inputRef.current?.value ?? "") : suggestion;
                      onSuggestionClick(compound ? inputRef.current?.value + mutatedSuggestion : mutatedSuggestion);
                      onValueInput && onValueInput(compound ? inputRef.current?.value + mutatedSuggestion : mutatedSuggestion);
                      inputRef?.current?.focus();
                      !compound && close();
                    }}
                    {...rover}
                  >
                    <OptionText>{suggestion}</OptionText>
                  </Option>
                ))}
              </SuggestionsDropdown>
            </Dropdown>
          </DropdownHandler>
        </DropdownContainer>
      )}
    </Wrapper>
  );
};
